<template>
  <header v-if="isPC" class="book-header">
    <a class="book-header__brand" href="#" @click.prevent="emit('home')">
      <SvgIcon
        :name="BOOK_ICON_NAMES.BOOK_HEADER_LOGO"
        label="云展绘本"
        :size="32"
        class="book-header__logo"
      />
      <span class="book-header__title">云展绘本</span>
    </a>

    <button type="button" class="book-header__desktop-cta" @click="emit('createSame')">
      <BookControlIcon
        :name="BOOK_ICON_NAMES.BOOK_HEADER_CTA"
        class="book-header__desktop-cta-icon"
      />
      制作同款
    </button>
  </header>

  <a
    v-else
    class="book-header__mobile-cta"
    :href="officialUrl"
    target="_top"
    aria-label="制作同款"
    @pointerdown.stop
    @pointerup.stop="navigateToOfficialWebsite"
    @pointercancel.stop
    @click.stop.prevent="navigateToOfficialWebsite"
  >
    <SvgIcon
      :name="BOOK_ICON_NAMES.BOOK_HEADER_LOGO"
      :size="28"
      class="book-header__mobile-cta-icon"
      aria-hidden="true"
    />
    <span>制作同款</span>
  </a>
</template>

<script setup lang="ts">
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { BOOK_ICON_NAMES } from '@/constants/svg';
  import BookControlIcon from './BookControlIcon.vue';

  defineProps<{
    isPC: boolean;
  }>();

  const configuredDomain = String(import.meta.env.VITE_WEB_DOMAIN ?? '').trim();
  const officialUrl = /^https?:\/\//i.test(configuredDomain)
    ? configuredDomain
    : `https://${configuredDomain}`;

  const navigateToOfficialWebsite = () => {
    try {
      if (window.top && window.top !== window) {
        window.top.location.href = officialUrl;
        return;
      }
    } catch {
      // 沙箱化 iframe 可能禁止顶层导航，此时退回当前阅读页跳转。
    }
    window.location.href = officialUrl;
  };

  const emit = defineEmits<{
    home: [];
    createSame: [];
  }>();
</script>

<style scoped>
  .book-header {
    width: 100%;
    height: 64px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    background: #fbf9ff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    box-sizing: border-box;
  }

  .book-header__brand {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }

  .book-header__logo {
    width: 32px;
    height: 32px;
  }

  .book-header__title {
    color: #1a1a2e;
    font-size: 18px;
    font-weight: 700;
  }

  .book-header__desktop-cta {
    height: 36px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 16px;
    color: #1a1a2e;
    background: #fff;
    border: 1px solid #e5e1f5;
    border-radius: 18px;
    font-size: 14px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .book-header__desktop-cta:hover {
    border-color: #6366f1;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
  }

  .book-header__desktop-cta:focus-visible,
  .book-header__mobile-cta:focus-visible {
    outline: 2px solid #6033e1;
    outline-offset: 3px;
  }

  .book-header__desktop-cta-icon {
    color: #6366f1;
    font-size: 15px;
  }

  .book-header__mobile-cta {
    position: absolute;
    right: 0;
    bottom: calc(24px + env(safe-area-inset-bottom));
    z-index: 31;
    width: 80px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 6px 11px;
    color: #6033e1;
    background: linear-gradient(90deg, rgba(246, 236, 252, 0.85), rgba(237, 232, 255, 0.85));
    border: 0;
    border-radius: 66px 0 0 66px;
    box-shadow: 0 0 4px rgba(96, 51, 225, 0.2);
    font-family: 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 20px;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .book-header__mobile-cta-icon {
    width: 28px;
    height: 28px;
  }

  .book-header__mobile-cta span {
    background: linear-gradient(100deg, #6033e1 0%, #6147ff 31%, #d451ff 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .book-header__mobile-cta:active {
    transform: scale(0.98);
    transform-origin: right center;
  }

  @media (prefers-reduced-motion: reduce) {
    .book-header__desktop-cta {
      transition: none;
    }
  }
</style>
